import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import BookingCalendar from '../../components/feature/BookingCalendar';
import { useSEO } from '../../utils/seo';

const TIMESLOTS_API = 'https://readdy.ai/api/public/calendar/timeslots/256e16b2-4096-4352-8a3a-936752b1f429.989ed8f90866958c45257206f90aca3ae58cd1bf70e3fe8bcef5057119053112';
const APPOINTMENTS_API = 'https://readdy.ai/api/public/calendar/appointments/256e16b2-4096-4352-8a3a-936752b1f429.989ed8f90866958c45257206f90aca3ae58cd1bf70e3fe8bcef5057119053112';

const calendarTexts = {
  title: 'Schedule a Consultation',
  subtitle: 'Book a time with Jeffery Long to discuss your real estate needs.',
  stepDateLabel: 'Select Date',
  stepTimeLabel: 'Select Time',
  stepConfirmLabel: 'Confirm',
  prevMonth: 'Previous month',
  nextMonth: 'Next month',
  selectDate: 'Please select a date to view available times',
  selectTime: 'Available Time Slots',
  reviewTitle: 'Your Appointment',
  selectedDateLabel: 'Date',
  selectedTimeLabel: 'Time',
  modifyTime: 'Change Time',
  nameLabel: 'Full Name',
  namePlaceholder: 'Enter your full name',
  phoneLabel: 'Phone Number',
  phonePlaceholder: '(555) 123-4567',
  notesLabel: 'Notes (optional)',
  notesPlaceholder: 'Any details about your inquiry — buying, selling, property type, etc.',
  submitButton: 'Confirm Appointment',
  submitting: 'Booking...',
  successMessage: 'Your appointment has been booked successfully! We will be in touch shortly.',
  errorMessage: 'Something went wrong. Please try again or call us directly.',
  noSlots: 'No available slots for this date',
  backButton: 'Back',
};

export default function BookingPage() {
  useSEO({
    title: 'Schedule a Consultation | Book Appointment with Jeffery Long | eXp Realty California',
    description: 'Schedule a free consultation with Jeffery Long - 40+ years real estate experience in Northern California. Virtual or in-person appointments available. Discuss buying, selling, or investing in Sacramento, El Dorado Hills, Cameron Park. CA DRE #02234432',
    keywords: 'schedule consultation, book appointment, real estate consultation, Jeffery Long appointment, Sacramento realtor meeting, free consultation, real estate advice, Northern California',
    canonical: '/booking',
    ogTitle: 'Schedule a Consultation | Book Appointment with Jeffery Long',
    ogDescription: 'Book a free consultation with Jeffery Long. 40+ years of real estate expertise. Virtual or in-person appointments available.',
    ogImage: 'https://public.readdy.ai/ai/img_res/6fc029fcd5a4657e28a8b5f9f6c22eb9.jpg',
    schema: {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Real Estate Consultation",
      "provider": {
        "@type": "RealEstateAgent",
        "name": "Jeffery Long - eXp Realty",
        "telephone": "+1-916-257-8244",
        "email": "realestatedevelopmentauthority@gmail.com"
      },
      "areaServed": {
        "@type": "State",
        "name": "California"
      },
      "description": "Free real estate consultation with Jeffery Long. Virtual or in-person appointments available for buying, selling, or investing in Northern California properties.",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "description": "Free Initial Consultation"
      }
    }
  });

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: "url('https://readdy.ai/api/search-image?query=elegant%20modern%20real%20estate%20office%20interior%20with%20warm%20natural%20lighting%2C%20minimalist%20decor%2C%20wooden%20accents%2C%20green%20plants%2C%20professional%20consultation%20setting%20with%20comfortable%20seating%20area%2C%20soft%20earth%20tones%20and%20cream%20colors&width=1400&height=600&seq=booking-hero-bg&orientation=landscape')",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50" />
          <div className="relative container mx-auto px-4 text-center w-full">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-white text-sm font-medium">Appointments Available</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 tracking-tight">
              Schedule Your Consultation
            </h1>
            <p className="text-lg text-white/85 max-w-2xl mx-auto leading-relaxed">
              Meet with Jeffery Long — over 40 years of real estate expertise in Northern California.
              Whether you are buying, selling, or investing, let us guide your next move.
            </p>
          </div>
        </section>

        {/* Booking Section */}
        <section className="py-16 bg-gray-50/50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {/* Info Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                <div className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-emerald-50">
                    <i className="ri-video-chat-line text-emerald-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Virtual or In-Person</p>
                    <p className="text-xs text-gray-500">Your choice of meeting format</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-emerald-50">
                    <i className="ri-shield-check-line text-emerald-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">No Obligation</p>
                    <p className="text-xs text-gray-500">Free initial consultation</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-emerald-50">
                    <i className="ri-calendar-check-line text-emerald-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Instant Confirmation</p>
                    <p className="text-xs text-gray-500">Receive details immediately</p>
                  </div>
                </div>
              </div>

              {/* Calendar Component */}
              <BookingCalendar
                timeslotsApi={TIMESLOTS_API}
                appointmentsApi={APPOINTMENTS_API}
                texts={calendarTexts}
              />
            </div>
          </div>
        </section>

        {/* Why Book Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h4 className="text-center text-xl font-semibold text-gray-800 mb-8">
                <a href="#why-book" id="why-book" className="hover:text-emerald-600 transition-colors">
                  Why Schedule a Consultation?
                </a>
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex gap-4 p-5 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-emerald-100">
                    <i className="ri-home-heart-line text-emerald-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm mb-1">Personalized Property Search</p>
                    <p className="text-gray-500 text-sm leading-relaxed">Get curated listings that match your exact criteria, including off-market opportunities.</p>
                  </div>
                </div>
                <div className="flex gap-4 p-5 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-emerald-100">
                    <i className="ri-line-chart-line text-emerald-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm mb-1">Market Analysis</p>
                    <p className="text-gray-500 text-sm leading-relaxed">Understand current pricing trends and neighborhood insights for smarter decisions.</p>
                  </div>
                </div>
                <div className="flex gap-4 p-5 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-emerald-100">
                    <i className="ri-money-dollar-circle-line text-emerald-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm mb-1">Financing Guidance</p>
                    <p className="text-gray-500 text-sm leading-relaxed">Explore mortgage options, down payment programs, and investment financing strategies.</p>
                  </div>
                </div>
                <div className="flex gap-4 p-5 rounded-xl bg-gray-50 border border-gray-100">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-emerald-100">
                    <i className="ri-auction-line text-emerald-600 text-lg"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm mb-1">Selling Strategy</p>
                    <p className="text-gray-500 text-sm leading-relaxed">Maximize your property value with expert staging, pricing, and marketing plans.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-12 bg-emerald-700">
          <div className="container mx-auto px-4 text-center">
            <p className="text-white/80 text-sm mb-2">Prefer to talk now?</p>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
              <a
                href="tel:916-257-8244"
                className="inline-flex items-center gap-2 bg-white text-emerald-700 px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                Call (916) 257-8244
              </a>
              <a
                href="mailto:realestatedevelopmentauthority@gmail.com"
                className="inline-flex items-center gap-2 border-2 border-white/40 text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-white/10 transition-colors whitespace-nowrap"
              >
                <i className="ri-mail-line"></i>
                Email Us
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
